import { createRouter, createWebHistory, type RouteLocationNormalized } from 'vue-router'
import WorkspaceLayout from '@/layouts/WorkspaceLayout.vue'
import TryLayout from '@/layouts/TryLayout.vue'
import AuthLayout from '@/layouts/AuthLayout.vue'
import { useAuthStore } from '@/stores/auth'
import { useAppStore } from '@/stores/app'
import { localeRoutePattern, localeToRouteSegment, routeSegmentToLocale } from '@/i18n'

const localeRoot = `/:locale(${localeRoutePattern})?`
const localePrefix = (to: RouteLocationNormalized) =>
  to.params.locale ? `/${String(to.params.locale)}` : ''
const legacyRedirect = (path: string) => (to: RouteLocationNormalized) => ({
  path: `${localePrefix(to)}${path}`,
  query: to.query,
})

const router = createRouter({
  history: createWebHistory(),
  scrollBehavior: () => ({ top: 0, left: 0 }),
  routes: [
    {
      path: `${localeRoot}/auth-bridge`,
      name: 'auth-bridge',
      component: () => import('@/pages/AuthBridgePage.vue'),
    },
    {
      path: `${localeRoot}/try`,
      component: TryLayout,
      children: [
        { path: '', name: 'try', component: () => import('@/pages/TryPage.vue') },
        {
          path: 'results',
          name: 'try-results',
          component: () => import('@/pages/SearchResultsPage.vue'),
          props: { tryMode: true },
        },
      ],
    },
    {
      path: localeRoot,
      component: WorkspaceLayout,
      meta: { requiresAuth: true },
      children: [
        { path: '', name: 'search', component: () => import('@/pages/SearchPage.vue') },
        { path: 'plans', name: 'plans', component: () => import('@/pages/PlansPage.vue') },
        {
          path: 'search/results',
          name: 'search-results',
          component: () => import('@/pages/SearchResultsPage.vue'),
        },
        { path: 'history', name: 'history', component: () => import('@/pages/HistoryPage.vue') },
        {
          path: 'commissions',
          name: 'commissions',
          component: () => import('@/pages/CommissionsPage.vue'),
        },
        {
          path: 'purchases',
          name: 'purchases',
          component: () => import('@/pages/PurchasesPage.vue'),
        },
        {
          path: 'favorites',
          name: 'favorites',
          component: () => import('@/pages/FavoritesPage.vue'),
        },
        { path: 'orders', name: 'orders', component: () => import('@/pages/OrdersPage.vue') },
        {
          path: 'orders/:orderNumber/pay',
          name: 'order-pay',
          component: () => import('@/pages/OrderPayPage.vue'),
        },
        { path: 'feedback', name: 'feedback', component: () => import('@/pages/FeedbackPage.vue') },
        {
          path: 'change-password',
          name: 'change-password',
          component: () => import('@/pages/ChangePasswordPage.vue'),
        },
        {
          path: 'reffer-bind',
          name: 'reffer-bind',
          component: () => import('@/pages/RefferBindPage.vue'),
        },
        {
          path: 'referrer-list',
          name: 'referrer-list',
          component: () => import('@/pages/ReferrerListPage.vue'),
        },
        {
          path: 'recommendations',
          name: 'recommendations',
          component: () => import('@/pages/RecommendationsPage.vue'),
        },
        {
          path: 'patents/:id',
          name: 'patent-detail',
          component: () => import('@/pages/PatentDetailPage.vue'),
        },
        {
          path: 'companies/:name',
          name: 'company-detail',
          component: () => import('@/pages/CompanyDetailPage.vue'),
        },
      ],
    },
    {
      path: localeRoot,
      component: AuthLayout,
      meta: { guestOnly: true },
      children: [
        { path: 'login', name: 'login', component: () => import('@/pages/auth/LoginPage.vue') },
        {
          path: 'register',
          name: 'register',
          component: () => import('@/pages/auth/RegisterPage.vue'),
        },
        {
          path: 'forgot-password',
          name: 'forgot-password',
          component: () => import('@/pages/auth/ForgotPasswordPage.vue'),
        },
        {
          path: 'thirdparty-login/account-link',
          name: 'account-link',
          component: () => import('@/pages/auth/AccountLinkPage.vue'),
        },
        {
          path: 'thirdparty-login/account-create',
          name: 'account-create',
          component: () => import('@/pages/auth/AccountCreatePage.vue'),
        },
      ],
    },
    {
      path: `${localeRoot}/thirdparty-login/auth`,
      name: 'third-party-auth',
      component: () => import('@/pages/auth/ThirdPartyAuthPage.vue'),
    },
    { path: `${localeRoot}/patent-imgResult`, redirect: legacyRedirect('/search/results') },
    { path: `${localeRoot}/patent-history`, redirect: legacyRedirect('/history') },
    { path: `${localeRoot}/commission-record`, redirect: legacyRedirect('/commissions') },
    { path: `${localeRoot}/purchase-list`, redirect: legacyRedirect('/purchases') },
    { path: `${localeRoot}/collect`, redirect: legacyRedirect('/favorites') },
    { path: `${localeRoot}/order-list`, redirect: legacyRedirect('/orders') },
    {
      path: `${localeRoot}/order-detail`,
      redirect: (to) => ({
        path: `${localePrefix(to)}/orders/${String(to.query.id || '')}/pay`,
        query: to.query,
      }),
    },
    {
      path: `${localeRoot}/patent-detail`,
      redirect: (to) => ({
        path: `${localePrefix(to)}/patents/${String(to.query.id || '')}`,
        query: to.query,
      }),
    },
    {
      path: `${localeRoot}/company-detail`,
      redirect: (to) => ({
        path: `${localePrefix(to)}/companies/${encodeURIComponent(String(to.query.name || ''))}`,
        query: to.query,
      }),
    },
    { path: `${localeRoot}/product-recommend`, redirect: legacyRedirect('/recommendations') },
    {
      path: `${localeRoot}/:pathMatch(.*)*`,
      name: 'not-found',
      component: () => import('@/pages/NotFoundPage.vue'),
    },
  ],
})

router.beforeEach((to) => {
  const auth = useAuthStore()
  const appStore = useAppStore()
  const rawQueryLocale = Array.isArray(to.query.lang) ? to.query.lang[0] : to.query.lang
  const queryLocale = routeSegmentToLocale(rawQueryLocale ? String(rawQueryLocale) : undefined)
  const routeLocale = routeSegmentToLocale(to.params.locale ? String(to.params.locale) : undefined)
  const activeLocale = queryLocale || routeLocale || appStore.locale
  const localeSegment = localeToRouteSegment(activeLocale)
  if (appStore.locale !== activeLocale) appStore.setLocale(activeLocale)

  // Let Vue Router preserve whether the original navigation used push or replace.
  if (!routeLocale || queryLocale || String(to.params.locale || '') !== localeSegment) {
    const { lang: _lang, ...query } = to.query
    if (to.name) {
      return {
        name: to.name,
        params: { ...to.params, locale: localeSegment },
        query,
        hash: to.hash,
      }
    }
    const path = to.path.replace(/^\/(?:zh|zh-cn|en|ja|ko|fr|de|ru|ar)(?=\/|$)/i, '') || '/'
    return {
      path: `/${localeSegment}${path === '/' ? '' : path}`,
      query,
      hash: to.hash,
    }
  }
  if (!auth.token) auth.init()
  if (to.matched.some((record) => record.meta.requiresAuth) && !auth.isLogin) {
    return { name: 'login', params: { locale: localeSegment }, query: { redirect: to.fullPath } }
  }
  if (to.matched.some((record) => record.meta.guestOnly) && auth.isLogin) {
    return { name: 'search', params: { locale: localeSegment } }
  }
})

router.afterEach((to) => {
  const title = typeof to.meta.title === 'string' ? to.meta.title : 'FindBao Console'
  document.title = title
})

export default router
